import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL, TOKEN } from "../../../variables/constants";
import Select from "react-select";
import countryList from "react-select-country-list";


const AddSmig = ({
  show,
  handleClose,
  editMode = false,
  selectedItem = null,
  items = [],
  onAdd,
  onUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    montantSmig: "",
    dateDebutApplication: "",
    dateFinApplication: "",
    pays: "",
  });

  const countryOptions = useMemo(() => {
    const countries = countryList().getData();
    return countries.map(({ label, value }) => ({
      value,
      label,
    }));
  }, []);

  // Préremplissage en mode édition
  useEffect(() => {
    if (editMode && selectedItem) {
      setFormData({
        montantSmig: selectedItem.montantSmig ?? "",
        dateDebutApplication: selectedItem.dateDebutApplication ?? "",
        dateFinApplication: selectedItem.dateFinApplication ?? "",
        pays: selectedItem.pays ?? "",
      });
    } else {
      setFormData({
        montantSmig: "",
        dateDebutApplication: "",
        dateFinApplication: "",
        pays: "",
      });
    }
  }, [editMode, selectedItem, show]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  // Validation simple + cohérence dates
  const validate = () => {
    const { montantSmig, dateDebutApplication, dateFinApplication, pays } =
      formData;
    if (!pays.trim()) {
      toast.warning("Le pays est requis.");
      return false;
    }
    if (
      !montantSmig ||
      Number.isNaN(Number(montantSmig)) ||
      Number(montantSmig) <= 0
    ) {
      toast.warning("Le montant SMIG doit être un nombre positif.");
      return false;
    }
    if (!dateDebutApplication) {
      toast.warning("La date de début d'application est requise.");
      return false;
    }
    if (dateFinApplication && dateFinApplication < dateDebutApplication) {
      toast.warning("La date de fin doit être postérieure à la date de début.");
      return false;
    }
    return true;
  };

  // Doublon: (pays normalisé, dateDebutApplication)
  const isDuplicateOnCreate = () => {
    const normPays = (formData.pays || "").trim().toLowerCase();
    const start = (formData.dateDebutApplication || "").trim();
    return items?.some(
      (it) =>
        (it.pays || "").trim().toLowerCase() === normPays &&
        (it.dateDebutApplication || "").trim() === start
    );
  };

  const resetAndClose = () => {
    setFormData({
      montantSmig: "",
      dateDebutApplication: "",
      dateFinApplication: "",
      pays: "",
    });
    handleClose && handleClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Contrôle de doublon uniquement en CREATE
    if (!editMode && isDuplicateOnCreate()) {
      toast.error("Un SMIG existe déjà pour ce pays et cette date de début.");
      return;
    }

    setLoading(true);
    const payloadBase = {
      montantSmig: Number(formData.montantSmig),
      dateDebutApplication: formData.dateDebutApplication,
      dateFinApplication: formData.dateFinApplication || null,
      pays: formData.pays.trim(),
    };

    const headers = {
      Authorization: `Bearer ${TOKEN}`,
      "Content-Type": "application/json",
    };

    try {
      if (editMode) {
        const payload = { ...selectedItem, ...payloadBase }; // inclut smigId
        const res = await axios.put(`${API_URL}/api/smig`, payload, {
          headers,
        });
        toast.success("SMIG modifié avec succès.");
        onUpdate && onUpdate(res.data);
      } else {
        const { /* smigId, */ ...createPayload } = payloadBase; // ne jamais envoyer l'id
        const res = await axios.post(`${API_URL}/api/smig`, createPayload, {
          headers,
        });
        toast.success("SMIG ajouté avec succès.");
        onAdd && onAdd(res.data);
      }
      resetAndClose();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement du SMIG.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Body>
        <button
          type="button"
          className="btn-close btn-primary"
          onClick={handleClose}
        />
        <div className="text-center mb-4">
          <h4>{editMode ? "Modifier" : "Ajouter"} un SMIG</h4>
          <p className="address-subtitle">
            {editMode ? "Mettre à jour" : "Créer"} un enregistrement SMIG
          </p>
        </div>

        <form className="row g-3" onSubmit={onSubmit}>
          <div className="col-md-6">
            <label className="form-label">Pays</label>
            <Select
              classNamePrefix="react-select"
              options={countryOptions}
              placeholder="Sélectionner un pays"
              isClearable
              value={
                countryOptions.find(
                  (option) =>
                    option.label.toLowerCase() ===
                    (formData.pays || "").toLowerCase()
                ) || null
              }
              onChange={(selected) =>
                setFormData((prev) => ({
                  ...prev,
                  pays: selected?.label || "",
                }))
              }
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "38px",
                }),
              }}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Montant SMIG</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              name="montantSmig"
              value={formData.montantSmig}
              onChange={onChange}
              required
              min="0"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Début d'application</label>
            <input
              type="date"
              className="form-control"
              name="dateDebutApplication"
              value={formData.dateDebutApplication}
              onChange={onChange}
              required
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Fin d'application (optionnel)</label>
            <input
              type="date"
              className="form-control"
              name="dateFinApplication"
              value={formData.dateFinApplication}
              onChange={onChange}
            />
          </div>

          <div className="col-12 d-flex gap-2 mt-3">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
            >
              Annuler
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddSmig;
